const baseUrl = process.env.REACT_APP_API_ENDPOINT;

export type AuthorizedResourceField = 'authorized_games' | 'authorized_ebooks';

export const PENDING_AUTHORIZED_GAME_KEY = 'pendingAuthorizedGameId';
export const PENDING_AUTHORIZED_EBOOK_KEY = 'pendingAuthorizedEbookId';

const toId = (value: any): number | null => {
  const id = Number(typeof value === 'object' ? value?.id : value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const readRelationIds = (raw: any): number[] => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
  return list.map(toId).filter((id: number | null): id is number => id !== null);
};

const pendingKey = (field: AuthorizedResourceField) =>
  field === 'authorized_games' ? PENDING_AUTHORIZED_GAME_KEY : PENDING_AUTHORIZED_EBOOK_KEY;

const claimFetch = async (
  path: string,
  method: 'GET' | 'PUT' | 'POST',
  body?: any,
  tokenOverride?: string
) => {
  const token = tokenOverride || localStorage.getItem('userToken');
  if (!token) {
    return null;
  }

  const response = await fetch(`${baseUrl}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(process.env.NODE_ENV === 'development' ? { 'ngrok-skip-browser-warning': '1' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    return null;
  }

  return response.status === 204 ? {} : response.json();
};

export const rememberPendingAuthorizedResource = (
  field: AuthorizedResourceField,
  resourceId: number
) => {
  if (!resourceId) {
    return;
  }
  sessionStorage.setItem(pendingKey(field), String(resourceId));
};

export const claimAuthorizedResourceIfMissing = async ({
  userId,
  resourceId,
  field,
  token,
}: {
  userId: number;
  resourceId: number;
  field: AuthorizedResourceField;
  token?: string;
}) => {
  if (!userId || !resourceId) {
    return;
  }

  if (field === 'authorized_ebooks') {
    const acquired = await claimFetch(
      '/api/create-ebook-result',
      'POST',
      {
        serious_ebook_id: resourceId,
        acquisition_source: 'share_link',
      },
      token
    );
    if (acquired?.id) sessionStorage.removeItem(pendingKey(field));
    return;
  }

  const payload = await claimFetch(
    `/api/users/${userId}?populate=${field}`,
    'GET',
    undefined,
    token
  );
  const user = payload?.id ? payload : payload?.data;
  if (!user?.id) {
    return;
  }

  const currentIds = readRelationIds(user[field]);
  if (currentIds.includes(resourceId)) {
    sessionStorage.removeItem(pendingKey(field));
    return;
  }

  const updated = await claimFetch(
    `/api/users/${userId}`,
    'PUT',
    { [field]: [...currentIds, resourceId] },
    token
  );
  if (updated) {
    sessionStorage.removeItem(pendingKey(field));
  }
};

export const claimShareResourcesAfterAuth = async ({
  userId,
  token,
  gameId,
  ebookId,
}: {
  userId: number;
  token?: string;
  gameId?: number | string | null;
  ebookId?: number | string | null;
}) => {
  const nextGameId = toId(gameId) || toId(sessionStorage.getItem(PENDING_AUTHORIZED_GAME_KEY));
  const nextEbookId = toId(ebookId) || toId(sessionStorage.getItem(PENDING_AUTHORIZED_EBOOK_KEY));

  if (nextGameId) {
    await claimAuthorizedResourceIfMissing({
      userId,
      resourceId: nextGameId,
      field: 'authorized_games',
      token,
    });
  }
  if (nextEbookId) {
    await claimAuthorizedResourceIfMissing({
      userId,
      resourceId: nextEbookId,
      field: 'authorized_ebooks',
      token,
    });
  }
};
